import * as React from "react";
import { styled } from "@mui/material/styles";
import MuiDrawer, { drawerClasses } from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";

import MenuContent, {
  COLLAPSED_SIDEBAR_WIDTH,
  EXPANDED_SIDEBAR_WIDTH,
  FIGMA_DASHBOARD_WIDTH,
  FIGMA_SIDEBAR_HEIGHT,
} from "./MenuContent";
import { useAuth } from "../../../../app/providers/AuthProvider";

const DESKTOP_SCALE_BASE_WIDTH = Math.min(FIGMA_DASHBOARD_WIDTH, 1280);

function getViewportScale() {
  if (typeof window === "undefined") return 1;
  const heightScale = window.innerHeight / FIGMA_SIDEBAR_HEIGHT;
  const widthScale = window.innerWidth >= DESKTOP_SCALE_BASE_WIDTH
    ? 1
    : window.innerWidth / DESKTOP_SCALE_BASE_WIDTH;
  const viewportScale = Math.min(heightScale, widthScale);

  return Math.min(1, Math.max(0.68, viewportScale));
}

const Drawer = styled(MuiDrawer)({
  flexShrink: 0,
  boxSizing: "border-box",
  [`& .${drawerClasses.paper}`]: {
    boxSizing: "border-box",
  },
});

export default function SideMenu() {
  const { isAuthenticated } = useAuth();
  const compactDesktop = useMediaQuery("(max-width:1180px), (max-height:700px)");
  const [scale, setScale] = React.useState(getViewportScale);
  const [expanded, setExpanded] = React.useState(() => !compactDesktop);
  const sidebarWidth = (expanded ? EXPANDED_SIDEBAR_WIDTH : COLLAPSED_SIDEBAR_WIDTH) * scale;
  const drawerReservedWidth = sidebarWidth;

  React.useEffect(() => {
    function syncScale() {
      setScale(getViewportScale());
    }

    syncScale();
    window.addEventListener("resize", syncScale);
    return () => window.removeEventListener("resize", syncScale);
  }, []);

  React.useEffect(() => {
    if (compactDesktop) {
      setExpanded(false);
    }
  }, [compactDesktop]);

  if (!isAuthenticated) return null;

  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: "none", md: "block" },
        width: `${drawerReservedWidth}px`,
        transition: "width 220ms ease",
        [`& .${drawerClasses.paper}`]: {
          backgroundColor: "transparent",
          border: 0,
          height: "100vh",
          left: 0,
          overflow: "visible",
          top: 0,
          transition: "width 220ms ease",
          width: `${sidebarWidth}px`,
        },
      }}
    >
      <MenuContent
        expanded={expanded}
        scale={scale}
        onToggleExpanded={() => setExpanded((current) => !current)}
      />
    </Drawer>
  );
}
